import React, { useEffect, useRef } from 'react';
import { Box } from '@chakra-ui/react';

interface Snowflake {
  x: number;
  y: number;
  size: number;
  speed: number;
  opacity: number;
  drift: number;
}

const SnowEffect: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const snowflakesRef = useRef<Snowflake[]>([]);
  const animationFrameRef = useRef<number>();

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // Create initial snowflakes
    const createSnowflake = (startY?: number): Snowflake => ({
      x: Math.random() * window.innerWidth,
      y: startY !== undefined ? startY : Math.random() * -window.innerHeight, // Start above screen, distributed
      size: Math.random() * 4 + 2, // 2-6px
      speed: Math.random() * 2 + 1, // 1-3px per frame
      opacity: Math.random() * 0.5 + 0.5, // 0.5-1.0
      drift: Math.random() * 2 - 1, // -1 to 1 (horizontal drift)
    });

    // Initialize snowflakes starting above the screen so they appear gradually
    const snowflakeCount = Math.min(50, Math.floor(window.innerWidth / 20));
    snowflakesRef.current = Array.from({ length: snowflakeCount }, () => createSnowflake());

    // Function to draw a snowflake shape
    const drawSnowflake = (ctx: CanvasRenderingContext2D, x: number, y: number, size: number) => {
      ctx.save();
      ctx.translate(x, y);
      
      // Draw 6 arms of the snowflake
      for (let i = 0; i < 6; i++) {
        ctx.rotate((Math.PI * 2) / 6);
        
        // Main arm
        ctx.beginPath();
        ctx.moveTo(0, 0);
        ctx.lineTo(0, -size);
        ctx.stroke();
        
        // Side branches
        ctx.beginPath();
        ctx.moveTo(0, -size * 0.3);
        ctx.lineTo(-size * 0.2, -size * 0.4);
        ctx.stroke();
        
        ctx.beginPath();
        ctx.moveTo(0, -size * 0.3);
        ctx.lineTo(size * 0.2, -size * 0.4);
        ctx.stroke();
        
        ctx.beginPath();
        ctx.moveTo(0, -size * 0.7);
        ctx.lineTo(-size * 0.15, -size * 0.8);
        ctx.stroke();
        
        ctx.beginPath();
        ctx.moveTo(0, -size * 0.7);
        ctx.lineTo(size * 0.15, -size * 0.8);
        ctx.stroke();
      }
      
      // Draw center circle
      ctx.beginPath();
      ctx.arc(0, 0, size * 0.15, 0, Math.PI * 2);
      ctx.fill();
      
      ctx.restore();
    };

    const animate = () => {
      if (!canvas) return;

      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      // Clear canvas
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Set drawing style for snowflakes
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.8)';
      ctx.fillStyle = 'rgba(255, 255, 255, 0.8)';
      ctx.lineWidth = 1;
      ctx.lineCap = 'round';

      // Update and draw snowflakes
      snowflakesRef.current.forEach((flake) => {
        // Update position
        flake.y += flake.speed;
        flake.x += flake.drift * 0.3;

        // Reset if off screen
        if (flake.y > window.innerHeight) {
          flake.y = -10;
          flake.x = Math.random() * window.innerWidth;
        }

        // Wrap horizontally
        if (flake.x < 0) flake.x = window.innerWidth;
        if (flake.x > window.innerWidth) flake.x = 0;

        // Draw snowflake with opacity
        ctx.save();
        ctx.globalAlpha = flake.opacity;
        drawSnowflake(ctx, flake.x, flake.y, flake.size);
        ctx.restore();
      });

      animationFrameRef.current = requestAnimationFrame(animate);
    };

    // Handle resize
    const handleResize = () => {
      if (!canvas) return;
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      
      // Adjust snowflake count based on window size
      const newCount = Math.min(50, Math.floor(window.innerWidth / 20));
      if (newCount > snowflakesRef.current.length) {
        const toAdd = newCount - snowflakesRef.current.length;
        for (let i = 0; i < toAdd; i++) {
          // New snowflakes start from the top
          snowflakesRef.current.push(createSnowflake(-10));
        }
      } else if (newCount < snowflakesRef.current.length) {
        snowflakesRef.current = snowflakesRef.current.slice(0, newCount);
      }
    };

    // Set initial canvas size
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    // Start animation
    animate();

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, []);

  return (
    <Box
      position="fixed"
      top={0}
      left={0}
      width="100%"
      height="100%"
      pointerEvents="none"
      zIndex={1}
    >
      <canvas
        ref={canvasRef}
        style={{
          display: 'block',
          width: '100%',
          height: '100%',
        }}
      />
    </Box>
  );
};

export default SnowEffect;
